import React, {useState, useMemo, useRef, useCallback} from 'react';
import styled, {keyframes} from 'styled-components';
import Select from 'react-select';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {familyTreeData} from '../../familyTreeData';
import {db} from '../../firebase';
import {ref as dbRef, push, set} from 'firebase/database';

const EMOJIS = [
    '😀', '😂', '🥰', '😍', '🤣', '😊', '😋', '🤔', '😎', '🙈',
    '👋', '❤️', '🎂', '🎉', '🎊', '🏠', '👨‍👩‍👧‍👦', '💪', '🌟', '⭐',
    '🎵', '🎶', '📸', '🏆', '🎮', '🍕', '☕', '🌊', '✈️', '🎁',
    '🤗', '😢', '🥳', '👏', '🙏', '💕', '🔥', '✨', '🎈', '🫶',
];

const TOOLBAR = [
    [{header: [2, 3, false]}],
    ['bold', 'italic', 'underline'],
    [{color: []}],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['clean'],
];

const buildPeopleOptions = (livingOnly) =>
    familyTreeData
        .filter(p => {
            const fn = p.data.firstName || '';
            if (!fn || fn === 'אב' || fn === 'אם') return false;
            if (livingOnly && (p.data.deathYear || p.data.memorialDate)) return false;
            return true;
        })
        .map(p => ({
            value: p.id,
            label: `${p.data.firstName} ${p.data.lastName || ''}`.trim(),
            avatar: p.data.avatar,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'he'));

const PersonOption = ({data, innerProps, isFocused}) => (
    <div {...innerProps} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
        background: isFocused ? 'rgba(139,92,246,0.2)' : 'transparent',
        cursor: 'pointer', direction: 'rtl',
    }}>
        {data.avatar ? (
            <img src={data.avatar} alt="" style={{width: 32, height: 32, borderRadius: '50%', objectFit: 'cover'}}/>
        ) : (
            <div style={{width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14}}>👤</div>
        )}
        <span style={{color: '#f1f5f9', fontSize: 16}}>{data.label}</span>
    </div>
);

const PersonSingleValue = ({data}) => (
    <div style={{display: 'flex', alignItems: 'center', gap: 8, direction: 'rtl'}}>
        {data.avatar ? (
            <img src={data.avatar} alt="" style={{width: 28, height: 28, borderRadius: '50%', objectFit: 'cover'}}/>
        ) : (<span>👤</span>)}
        <span style={{color: '#f1f5f9'}}>{data.label}</span>
    </div>
);

const selectStyles = {
    control: (base, state) => ({
        ...base, borderRadius: 12,
        border: `2px solid ${state.isFocused ? 'rgba(139,92,246,0.8)' : 'rgba(139,92,246,0.3)'}`,
        background: 'rgba(255,255,255,0.06)', minHeight: 48, boxShadow: 'none',
        '&:hover': {borderColor: 'rgba(139,92,246,0.6)'},
    }),
    menu: (base) => ({...base, borderRadius: 12, background: '#1e1b4b', border: '1px solid rgba(139,92,246,0.3)', overflow: 'hidden', zIndex: 10}),
    menuList: (base) => ({...base, padding: 0, maxHeight: 200}),
    input: (base) => ({...base, color: '#f1f5f9'}),
    placeholder: (base) => ({...base, color: 'rgba(241,245,249,0.4)', direction: 'rtl'}),
    singleValue: (base) => ({...base, color: '#f1f5f9'}),
    noOptionsMessage: (base) => ({...base, color: 'rgba(241,245,249,0.5)', direction: 'rtl'}),
    indicatorSeparator: () => ({display: 'none'}),
    dropdownIndicator: (base) => ({...base, color: 'rgba(139,92,246,0.6)'}),
    clearIndicator: (base) => ({...base, color: 'rgba(239,68,68,0.6)', '&:hover': {color: '#ef4444'}}),
};

const compressToDataUrl = (file, maxWidth = 800) =>
    new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            let {width, height} = img;
            if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.8));
        };
        img.src = URL.createObjectURL(file);
    });

const stripHtml = (html) => {
    if (!html) return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
};

const loadSavedAuthor = (options) => {
    const id = sessionStorage.getItem('trivia_author');
    if (!id) return null;
    return options.find(o => o.value === id) || null;
};

export const TriviaAdd = ({onBack}) => {
    const livingOpts = useMemo(() => buildPeopleOptions(true), []);
    const [addedBy, setAddedByRaw] = useState(() => loadSavedAuthor(livingOpts));
    const setAddedBy = useCallback((val) => {
        setAddedByRaw(val);
        if (val) sessionStorage.setItem('trivia_author', val.value);
        else sessionStorage.removeItem('trivia_author');
    }, []);
    const [answerType, setAnswerType] = useState('person');
    const [aboutPerson, setAboutPerson] = useState(null);
    const [wrongOptions, setWrongOptions] = useState([null, null, null]);
    const [textCorrect, setTextCorrect] = useState('');
    const [textWrong, setTextWrong] = useState(['', '', '']);
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState('');
    const [emojiTarget, setEmojiTarget] = useState(null);
    const [questionImages, setQuestionImages] = useState([]);
    const [answerImages, setAnswerImages] = useState([]);

    const questionRef = useRef(null);
    const answerRef = useRef(null);

    const allOptions = useMemo(() => buildPeopleOptions(false), []);
    const quillModules = useMemo(() => ({toolbar: TOOLBAR}), []);

    const removeImageFromEditor = useCallback((editorRef, url) => {
        const quill = editorRef.current?.getEditor();
        if (!quill) return;
        const delta = quill.getContents();
        let index = 0;
        for (const op of delta.ops) {
            if (op.insert?.image === url) {
                quill.deleteText(index, 1);
                break;
            }
            index += typeof op.insert === 'string' ? op.insert.length : 1;
        }
    }, []);

    const removeQuestionImage = useCallback((url) => {
        setQuestionImages(prev => prev.filter(u => u !== url));
        removeImageFromEditor(questionRef, url);
    }, [removeImageFromEditor]);

    const removeAnswerImage = useCallback((url) => {
        setAnswerImages(prev => prev.filter(u => u !== url));
        removeImageFromEditor(answerRef, url);
    }, [removeImageFromEditor]);

    const handleAddImage = useCallback((target) => {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();
        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            const ref = target === 'question' ? questionRef : answerRef;
            const setImages = target === 'question' ? setQuestionImages : setAnswerImages;
            const quill = ref.current?.getEditor();
            try {
                const url = await compressToDataUrl(file);
                if (quill) {
                    const range = quill.getSelection(true) || {index: quill.getLength() - 1};
                    quill.insertEmbed(range.index, 'image', url);
                    quill.setSelection(range.index + 1);
                }
                setImages(prev => [...prev, url]);
            } catch (err) {
                console.error('Image upload failed:', err);
            }
        };
    }, []);

    const insertEmoji = useCallback((target, emoji) => {
        const ref = target === 'question' ? questionRef : answerRef;
        const quill = ref.current?.getEditor();
        if (!quill) return;
        quill.focus();
        const range = quill.getSelection(true);
        quill.insertText(range.index, emoji);
        quill.setSelection(range.index + emoji.length);
        setEmojiTarget(null);
    }, []);

    const handleSave = async () => {
        if (!addedBy) { setError('יש לבחור מי את/ה'); return; }
        if (!stripHtml(question).trim()) { setError('יש למלא את שאלת הטריוויה'); return; }
        if (!stripHtml(answer).trim()) { setError('יש למלא את תשובת הטריוויה'); return; }

        if (answerType === 'person') {
            if (!aboutPerson) { setError('יש לבחור את התשובה הנכונה (בן משפחה)'); return; }
            const filledWrong = wrongOptions.filter(Boolean);
            if (filledWrong.length < 3) { setError('יש לבחור 3 תשובות שגויות'); return; }
        } else {
            if (!textCorrect.trim()) { setError('יש למלא את התשובה הנכונה'); return; }
            if (textWrong.some(t => !t.trim())) { setError('יש למלא את כל 3 התשובות השגויות'); return; }
        }

        setSaving(true);
        setError('');

        try {
            const item = {
                addedBy: addedBy.value,
                addedByName: addedBy.label,
                question,
                answer,
                answerType,
                createdAt: Date.now(),
            };

            if (answerType === 'person') {
                item.aboutPerson = aboutPerson.value;
                item.aboutPersonName = aboutPerson.label;
                item.wrongOptions = wrongOptions.filter(Boolean).map(o => ({id: o.value, name: o.label}));
            } else {
                item.textCorrect = textCorrect.trim();
                item.textWrong = textWrong.map(t => t.trim());
            }

            const itemRef = push(dbRef(db, 'trivia/items'));
            await set(itemRef, item);
            setSaved(true);
        } catch (err) {
            console.error('Save failed:', err);
            setError('שגיאה בשמירה, נסו שוב');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setAnswerType('person');
        setAboutPerson(null);
        setWrongOptions([null, null, null]);
        setTextCorrect('');
        setTextWrong(['', '', '']);
        setQuestion('');
        setAnswer('');
        setSaved(false);
        setError('');
        setEmojiTarget(null);
        setQuestionImages([]);
        setAnswerImages([]);
    };

    if (saved) {
        return (
            <Wrapper>
                <SuccessBox>
                    <SuccessEmoji>🎉</SuccessEmoji>
                    <SuccessText>הטריוויה נשמרה בהצלחה!</SuccessText>
                    <BtnRow>
                        <ActionBtn onClick={resetForm} $color="#8b5cf6">הוסיפו עוד</ActionBtn>
                        <ActionBtn onClick={onBack} $color="#64748b">חזרה לתפריט</ActionBtn>
                    </BtnRow>
                </SuccessBox>
            </Wrapper>
        );
    }

    return (
        <Wrapper>
            <TopBar>
                <BackBtn onClick={onBack}>→ חזרה</BackBtn>
                <PageTitle>הוסיפו טריוויה חדשה</PageTitle>
            </TopBar>

            <FormCard>
                <FieldGroup>
                    <Label>מי את/ה? *</Label>
                    <Select
                        value={addedBy}
                        onChange={setAddedBy}
                        options={livingOpts}
                        placeholder="בחרו את עצמכם..."
                        isRtl isSearchable
                        noOptionsMessage={() => 'לא נמצאו תוצאות'}
                        styles={selectStyles}
                        components={{Option: PersonOption, SingleValue: PersonSingleValue}}
                    />
                </FieldGroup>

                <FieldGroup>
                    <Label>שאלת הטריוויה *</Label>
                    <QuillWrapper>
                        <ReactQuill
                            ref={questionRef}
                            value={question}
                            onChange={setQuestion}
                            modules={quillModules}
                            theme="snow"
                            placeholder="כתבו את השאלה..."
                        />
                    </QuillWrapper>
                    <BottomBar>
                        <BarButtons>
                            <EmojiToggle onClick={() => setEmojiTarget(emojiTarget === 'question' ? null : 'question')}>
                                😊 אימוג׳י
                            </EmojiToggle>
                            <AddImageBtn onClick={() => handleAddImage('question')}>
                                📷 הוספת תמונה
                            </AddImageBtn>
                        </BarButtons>
                        {emojiTarget === 'question' && (
                            <EmojiGrid>
                                {EMOJIS.map(e => (
                                    <EmojiBtn key={e} onClick={() => insertEmoji('question', e)}>{e}</EmojiBtn>
                                ))}
                            </EmojiGrid>
                        )}
                        {questionImages.length > 0 && (
                            <ImagePreviewStrip>
                                {questionImages.map(url => (
                                    <ThumbWrap key={url}>
                                        <Thumb src={url} alt=""/>
                                        <ThumbRemove onClick={() => removeQuestionImage(url)}>✕</ThumbRemove>
                                    </ThumbWrap>
                                ))}
                            </ImagePreviewStrip>
                        )}
                    </BottomBar>
                </FieldGroup>

                <FieldGroup>
                    <Label>תשובת הטריוויה *</Label>
                    <QuillWrapper>
                        <ReactQuill
                            ref={answerRef}
                            value={answer}
                            onChange={setAnswer}
                            modules={quillModules}
                            theme="snow"
                            placeholder="כתבו את התשובה..."
                        />
                    </QuillWrapper>
                    <BottomBar>
                        <BarButtons>
                            <EmojiToggle onClick={() => setEmojiTarget(emojiTarget === 'answer' ? null : 'answer')}>
                                😊 אימוג׳י
                            </EmojiToggle>
                            <AddImageBtn onClick={() => handleAddImage('answer')}>
                                📷 הוספת תמונה
                            </AddImageBtn>
                        </BarButtons>
                        {emojiTarget === 'answer' && (
                            <EmojiGrid>
                                {EMOJIS.map(e => (
                                    <EmojiBtn key={e} onClick={() => insertEmoji('answer', e)}>{e}</EmojiBtn>
                                ))}
                            </EmojiGrid>
                        )}
                        {answerImages.length > 0 && (
                            <ImagePreviewStrip>
                                {answerImages.map(url => (
                                    <ThumbWrap key={url}>
                                        <Thumb src={url} alt=""/>
                                        <ThumbRemove onClick={() => removeAnswerImage(url)}>✕</ThumbRemove>
                                    </ThumbWrap>
                                ))}
                            </ImagePreviewStrip>
                        )}
                    </BottomBar>
                </FieldGroup>

                <Divider/>

                <FieldGroup>
                    <Label>סוג התשובה *</Label>
                    <ToggleRow>
                        <ToggleBtn $active={answerType === 'person'} onClick={() => setAnswerType('person')}>
                            👤 בן/בת משפחה
                        </ToggleBtn>
                        <ToggleBtn $active={answerType === 'text'} onClick={() => setAnswerType('text')}>
                            ✏️ טקסט חופשי
                        </ToggleBtn>
                    </ToggleRow>
                </FieldGroup>

                {answerType === 'person' ? (
                    <>
                        <FieldGroup>
                            <Label>התשובה הנכונה (בן/בת משפחה) *</Label>
                            <Select
                                value={aboutPerson}
                                onChange={setAboutPerson}
                                options={allOptions}
                                placeholder="בחרו את התשובה הנכונה..."
                                isRtl isSearchable isClearable
                                noOptionsMessage={() => 'לא נמצאו תוצאות'}
                                styles={selectStyles}
                                components={{Option: PersonOption, SingleValue: PersonSingleValue}}
                            />
                        </FieldGroup>

                        <FieldGroup>
                            <Label>3 תשובות שגויות *</Label>
                            <WrongOptionsHint>בחרו 3 בני משפחה שיופיעו כאפשרויות שגויות</WrongOptionsHint>
                            {wrongOptions.map((opt, i) => (
                                <Select
                                    key={i}
                                    value={opt}
                                    onChange={val => {
                                        const next = [...wrongOptions];
                                        next[i] = val;
                                        setWrongOptions(next);
                                    }}
                                    options={allOptions.filter(o =>
                                        o.value !== aboutPerson?.value &&
                                        !wrongOptions.some((w, wi) => wi !== i && w?.value === o.value)
                                    )}
                                    placeholder={`תשובה שגויה ${i + 1}...`}
                                    isRtl isSearchable isClearable
                                    noOptionsMessage={() => 'לא נמצאו תוצאות'}
                                    styles={selectStyles}
                                    components={{Option: PersonOption, SingleValue: PersonSingleValue}}
                                />
                            ))}
                        </FieldGroup>
                    </>
                ) : (
                    <>
                        <FieldGroup>
                            <Label>התשובה הנכונה *</Label>
                            <TextInput
                                value={textCorrect}
                                onChange={e => setTextCorrect(e.target.value)}
                                placeholder="הקלידו את התשובה הנכונה..."
                            />
                        </FieldGroup>

                        <FieldGroup>
                            <Label>3 תשובות שגויות *</Label>
                            <WrongOptionsHint>הקלידו 3 תשובות שגויות שיופיעו כאפשרויות</WrongOptionsHint>
                            {textWrong.map((val, i) => (
                                <TextInput
                                    key={i}
                                    value={val}
                                    onChange={e => {
                                        const next = [...textWrong];
                                        next[i] = e.target.value;
                                        setTextWrong(next);
                                    }}
                                    placeholder={`תשובה שגויה ${i + 1}...`}
                                />
                            ))}
                        </FieldGroup>
                    </>
                )}

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? <Spinner/> : 'שמרו 💾'}
                </SaveBtn>
            </FormCard>
        </Wrapper>
    );
};

const slideIn = keyframes`
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
`;

const spin = keyframes`
    to { transform: rotate(360deg); }
`;

const popIn = keyframes`
    0% { transform: scale(0.5); opacity: 0; }
    70% { transform: scale(1.1); }
    100% { transform: scale(1); opacity: 1; }
`;

const Wrapper = styled.div`
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 620px;
    padding: 20px 16px 80px;
    animation: ${slideIn} 0.4s ease-out;
`;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
`;

const BackBtn = styled.button`
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 14px;
    padding: 8px 16px;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.2s;
    white-space: nowrap;
    &:hover { background: rgba(255, 255, 255, 0.15); }
`;

const PageTitle = styled.h2`
    color: #f1f5f9;
    font-size: 22px;
    font-weight: 700;
    margin: 0;
`;

const FormCard = styled.div`
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(139, 92, 246, 0.2);
    border-radius: 20px;
    padding: 28px 24px;
    backdrop-filter: blur(12px);
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const FieldGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Label = styled.label`
    color: rgba(241, 245, 249, 0.85);
    font-size: 15px;
    font-weight: 600;
`;


const WrongOptionsHint = styled.div`
    color: rgba(241, 245, 249, 0.4);
    font-size: 13px;
    margin-bottom: 4px;
`;

const ToggleRow = styled.div`
    display: flex;
    gap: 8px;
`;

const ToggleBtn = styled.button`
    flex: 1;
    padding: 10px 16px;
    border-radius: 12px;
    border: 2px solid ${p => p.$active ? 'rgba(139,92,246,0.8)' : 'rgba(139,92,246,0.25)'};
    background: ${p => p.$active ? 'rgba(139,92,246,0.25)' : 'rgba(255,255,255,0.04)'};
    color: ${p => p.$active ? '#e0d4fc' : 'rgba(241,245,249,0.5)'};
    font-family: 'Heebo', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { border-color: rgba(139,92,246,0.6); }
`;

const TextInput = styled.input`
    width: 100%;
    padding: 12px 16px;
    border: 2px solid rgba(139,92,246,0.3);
    border-radius: 12px;
    background: rgba(255,255,255,0.06);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    direction: rtl;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;
    &:focus { border-color: rgba(139,92,246,0.8); }
    &::placeholder { color: rgba(241,245,249,0.3); }
`;

const Divider = styled.div`
    height: 1px;
    background: rgba(139, 92, 246, 0.15);
    margin: 4px 0;
`;

const QuillWrapper = styled.div`
    border: 2px solid rgba(139, 92, 246, 0.3);
    border-radius: 12px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.04);
    transition: border-color 0.2s;
    &:focus-within { border-color: rgba(139, 92, 246, 0.8); }

    .ql-toolbar.ql-snow {
        border: none;
        border-bottom: 1px solid rgba(139, 92, 246, 0.15);
        background: rgba(0, 0, 0, 0.2);
        direction: ltr;
    }
    .ql-toolbar .ql-stroke { stroke: rgba(241, 245, 249, 0.5); }
    .ql-toolbar .ql-fill { fill: rgba(241, 245, 249, 0.5); }
    .ql-toolbar .ql-picker-label { color: rgba(241, 245, 249, 0.5); }
    .ql-toolbar button:hover .ql-stroke,
    .ql-toolbar .ql-picker-label:hover .ql-stroke { stroke: #c4b5fd; }
    .ql-toolbar button:hover .ql-fill,
    .ql-toolbar .ql-picker-label:hover .ql-fill { fill: #c4b5fd; }
    .ql-toolbar button.ql-active .ql-stroke { stroke: #8b5cf6; }
    .ql-toolbar button.ql-active .ql-fill { fill: #8b5cf6; }
    .ql-toolbar .ql-picker-options {
        background: #1e1b4b;
        border: 1px solid rgba(139, 92, 246, 0.3);
        border-radius: 8px;
    }
    .ql-toolbar .ql-picker-item { color: #f1f5f9; }
    .ql-toolbar .ql-picker-item:hover { color: #c4b5fd; }

    .ql-container.ql-snow {
        border: none;
        font-family: 'Heebo', sans-serif;
        font-size: 16px;
        color: #f1f5f9;
    }
    .ql-editor {
        direction: rtl;
        text-align: right;
        min-height: 100px;
        line-height: 1.6;
        padding: 14px;
    }
    .ql-editor.ql-blank::before {
        color: rgba(241, 245, 249, 0.3);
        direction: rtl;
        text-align: right;
        font-style: normal;
    }
    .ql-editor img {
        display: none;
    }
    .ql-snow .ql-tooltip {
        background: #1e1b4b;
        border: 1px solid rgba(139, 92, 246, 0.3);
        color: #f1f5f9;
        border-radius: 8px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
    }
    .ql-snow .ql-tooltip input[type="text"] {
        background: rgba(255, 255, 255, 0.06);
        border: 1px solid rgba(139, 92, 246, 0.3);
        color: #f1f5f9;
        border-radius: 6px;
    }
`;

const BottomBar = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const BarButtons = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const EmojiToggle = styled.button`
    background: rgba(139, 92, 246, 0.12);
    border: 1px solid rgba(139, 92, 246, 0.3);
    color: #c4b5fd;
    font-family: 'Heebo', sans-serif;
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: rgba(139, 92, 246, 0.25); }
`;

const AddImageBtn = styled.button`
    background: rgba(59, 130, 246, 0.12);
    border: 1px solid rgba(59, 130, 246, 0.3);
    color: #93c5fd;
    font-family: 'Heebo', sans-serif;
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: rgba(59, 130, 246, 0.25); }
`;

const EmojiGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    padding: 10px;
    margin-top: 6px;
    background: rgba(30, 27, 75, 0.95);
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 12px;
    max-width: 320px;
    animation: ${slideIn} 0.15s ease;
`;

const EmojiBtn = styled.button`
    width: 36px;
    height: 36px;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    font-size: 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
    &:hover {
        background: rgba(139, 92, 246, 0.2);
        border-color: rgba(139, 92, 246, 0.4);
        transform: scale(1.2);
    }
`;

const ImagePreviewStrip = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 0;
`;

const ThumbWrap = styled.div`
    position: relative;
    width: 72px;
    height: 72px;
    border-radius: 10px;
    overflow: visible;
    flex-shrink: 0;
`;

const Thumb = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
    border: 2px solid rgba(139, 92, 246, 0.35);
`;

const ThumbRemove = styled.button`
    position: absolute;
    top: -8px;
    right: -8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    background: #ef4444;
    color: white;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    transition: transform 0.15s, background 0.15s;
    line-height: 1;
    padding: 0;

    &:hover {
        background: #dc2626;
        transform: scale(1.15);
    }
`;

const ErrorMsg = styled.div`
    color: #fca5a5;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    padding: 10px 16px;
    border-radius: 10px;
    font-size: 14px;
    text-align: center;
`;

const SaveBtn = styled.button`
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 14px;
    background: linear-gradient(135deg, #8b5cf6, #6d28d9);
    color: white;
    font-family: 'Heebo', sans-serif;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.25s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    &:hover:not(:disabled) { box-shadow: 0 0 24px rgba(139, 92, 246, 0.5); transform: translateY(-2px); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Spinner = styled.div`
    width: 22px;
    height: 22px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: ${spin} 0.6s linear infinite;
`;

const SuccessBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    gap: 20px;
    animation: ${popIn} 0.5s ease-out;
`;

const SuccessEmoji = styled.div`
    font-size: 64px;
`;

const SuccessText = styled.div`
    color: #a7f3d0;
    font-size: 24px;
    font-weight: 700;
`;

const BtnRow = styled.div`
    display: flex;
    gap: 12px;
    margin-top: 8px;
`;

const ActionBtn = styled.button`
    padding: 12px 24px;
    border: 2px solid ${p => p.$color};
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    color: #f1f5f9;
    font-family: 'Heebo', sans-serif;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: ${p => p.$color}; }
`;
